import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useEffect, useMemo, useRef } from 'react';
import { Control, FieldPathByValue, FieldValues, useController } from 'react-hook-form';
import { toast } from 'react-toastify';

import { DirectoryPayeeFragment, GetPayeesForCompanyDocument, PayeeType } from '@willow/graphql-iso/src/app';
import { addressToString } from '@willow/shared-iso';
import { CompanyId, PayeeId } from '@willow/types-iso';

import { ControlledSelect } from '../FormElements/Select/ControlledSelect';

const PAYEE_TYPE_LABELS: Record<PayeeType, string> = {
  [PayeeType.Tax]: 'Tax',
  [PayeeType.Insurance]: 'Insurance',
  [PayeeType.MortgageInsurance]: 'Mortgage Insurance',
  [PayeeType.Other]: 'Other',
};

interface Props<T extends FieldValues> {
  control: Control<T>;
  fieldName: FieldPathByValue<T, PayeeId | undefined | null>;
  companyId: CompanyId;
  payeeType: PayeeType;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  onPayeeSelected?: (payee: DirectoryPayeeFragment | undefined) => void;
  // Tax escrow settings rely on payee due dates, which "Other" payees don't carry — those
  // callers pass `false` to keep generic payees out of the list. Defaults to true so other
  // escrow forms continue to surface "Other" alongside the type-specific list.
  includeOtherPayees?: boolean;
  // Payee IDs to render as disabled (greyed out, not selectable). Used to surface the
  // "one active escrow setting per payee per loan" invariant in the escrow setting form.
  disabledPayeeIds?: ReadonlySet<PayeeId>;
}

export function PayeePicker<T extends FieldValues>({
  control,
  fieldName,
  companyId,
  payeeType,
  label,
  required,
  disabled,
  placeholder = 'Select a payee',
  onPayeeSelected,
  includeOtherPayees = true,
  disabledPayeeIds,
}: Props<T>) {
  const payeeTypes = useMemo(() => {
    if (payeeType === PayeeType.Other) return [PayeeType.Other];
    return includeOtherPayees ? [payeeType, PayeeType.Other] : [payeeType];
  }, [payeeType, includeOtherPayees]);

  const { data, loading, error } = useQuery(GetPayeesForCompanyDocument, {
    variables: {
      companyId,
      input: {
        take: 1000,
        skip: 0,
        payeeTypes,
      },
    },
  });

  useEffect(() => {
    if (error) {
      Sentry.captureException(error);
      toast.error('Failed to load payees. Please try again.');
    }
  }, [error]);

  const payees = useMemo<DirectoryPayeeFragment[]>(
    () => data?.company?.payees?.results ?? [],
    [data?.company?.payees?.results],
  );

  const options = useMemo(() => {
    const toOption = (payee: DirectoryPayeeFragment) => {
      const baseLabel = payee.address ? `${payee.name} — ${addressToString(payee.address)}` : payee.name;
      const isDisabled = disabledPayeeIds?.has(payee.id) ?? false;
      return {
        value: payee.id,
        label: isDisabled ? `${baseLabel} (already used on this loan)` : baseLabel,
        disabled: isDisabled,
      };
    };

    if (payeeType === PayeeType.Other) {
      return payees.map(toOption);
    }

    const primary = payees.filter((p) => p.payeeType === payeeType).map(toOption);
    if (!includeOtherPayees) return primary;

    const other = payees.filter((p) => p.payeeType === PayeeType.Other).map(toOption);

    return [
      { label: PAYEE_TYPE_LABELS[payeeType], options: primary },
      { label: PAYEE_TYPE_LABELS[PayeeType.Other], options: other },
    ].filter((group) => group.options.length > 0);
  }, [payees, payeeType, includeOtherPayees, disabledPayeeIds]);

  const { field } = useController({ control, name: fieldName });
  const selectedId = field.value as PayeeId | undefined | null;

  // Notify the consumer whenever the selected payee resolves to a record in the loaded set.
  // We track the last id we reported so we don't re-fire on every render.
  const lastReportedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!onPayeeSelected) return;
    const reportedId = selectedId ?? null;
    const match = selectedId ? payees.find((p) => p.id === selectedId) : undefined;
    // Only report once we either have the match or the user clears the field, to avoid
    // emitting `undefined` on the initial render before the query settles.
    if (lastReportedIdRef.current === reportedId && match) return;
    if (selectedId && !match && !loading) {
      lastReportedIdRef.current = reportedId;
      onPayeeSelected(undefined);
      return;
    }
    if (!selectedId) {
      if (lastReportedIdRef.current !== null) {
        lastReportedIdRef.current = null;
        onPayeeSelected(undefined);
      }
      return;
    }
    if (match) {
      lastReportedIdRef.current = reportedId;
      onPayeeSelected(match);
    }
  }, [selectedId, payees, loading, onPayeeSelected]);

  // PayeeId is a branded string. ControlledSelect's `fieldName` is typed against unbranded
  // primitive value types — cast through unknown so the controlled select can drive the branded
  // id field without TS narrowing through the FieldPathByValue helper.
  const selectFieldName = fieldName as unknown as FieldPathByValue<T, boolean | string | number | undefined | null>;

  return (
    <ControlledSelect
      control={control}
      fieldName={selectFieldName}
      options={options}
      label={label}
      required={required}
      disabled={disabled}
      placeholder={placeholder}
    />
  );
}
